import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw, Play, FastForward, RotateCcw } from "lucide-react";

const TowerOfHanoiGame = ({ onBack }) => {
  const navigate = useNavigate();

  // Settings
  const [numDisks, setNumDisks] = useState(3);
  
  // Game State
  // Towers: Array of 3 arrays. Each array contains disk sizes (integer).
  // Larger int = Larger disk.
  const [towers, setTowers] = useState([[], [], []]);
  const [selectedTower, setSelectedTower] = useState(null);
  const [moves, setMoves] = useState(0);
  const [isSolving, setIsSolving] = useState(false);
  const [gameWon, setGameWon] = useState(false);
  
  const minMoves = Math.pow(2, numDisks) - 1;

  // Initialize Game
  useEffect(() => {
    resetGame(numDisks);
  }, [numDisks]); // Re-init when disk count changes

  const resetGame = (n) => {
    // Top is end of array
    // [3, 2, 1] -> 1 is smallest and on top
    const startTower = [];
    for (let i = n; i >= 1; i--) {
        startTower.push(i);
    }
    setTowers([startTower, [], []]);
    setSelectedTower(null);
    setMoves(0);
    setGameWon(false);
    setIsSolving(false);
    stopAutoSolve();
  };

  // --------------------------------------------------------------------------
  // Core Logic
  // --------------------------------------------------------------------------

  const isValidMove = (fromTowerIdx, toTowerIdx) => {
      const fromStack = towers[fromTowerIdx];
      const toStack = towers[toTowerIdx];

      if (fromStack.length === 0) return false; // Empty source
      if (toStack.length === 0) return true;    // Empty dest is always valid

      const diskToMove = fromStack[fromStack.length - 1];
      const topDiskAtDest = toStack[toStack.length - 1];

      return diskToMove < topDiskAtDest; // Must be smaller than top
  };

  const executeMove = (fromIdx, toIdx) => {
      setTowers(prev => {
          const newTowers = prev.map(t => [...t]);
          const disk = newTowers[fromIdx].pop();
          newTowers[toIdx].push(disk);
          
          // Check Win
          // If last tower has all disks (and logic ensures they are sorted), we win
          if (toIdx === 2 && newTowers[2].length === numDisks) {
              setGameWon(true);
              setIsSolving(false); // Stop solver if we accidentally helped it finish
          }
          return newTowers;
      });
      setMoves(prev => prev + 1);
  };

  const handleTowerClick = (idx) => {
      if (gameWon || isSolving) return;

      if (selectedTower === null) {
          // Select
          if (towers[idx].length > 0) {
              setSelectedTower(idx);
          }
      } else {
          // Move or Deselect
          if (selectedTower === idx) {
              setSelectedTower(null); // Deselect
          } else {
              // Try move
              if (isValidMove(selectedTower, idx)) {
                  executeMove(selectedTower, idx);
                  setSelectedTower(null);
              } else {
                  // Invalid move, maybe just select this one instead if it has disks?
                  // UX choice: invalid flash or just change selection?
                  // Let's Just change selection if clicked tower has disks, else nothing
                  if (towers[idx].length > 0 && isValidMove(idx, selectedTower)) {
                      // Actually, users might want to swap selection
                      setSelectedTower(idx); 
                  } else if (towers[idx].length > 0) {
                      setSelectedTower(idx);
                  } else {
                    setSelectedTower(null); // Clicked empty invalid slot
                  }
              }
          }
      }
  };

  // --------------------------------------------------------------------------
  // Solver Logic (Recursive Visualizer)
  // --------------------------------------------------------------------------
  
  const solveQueueRef = useRef([]);
  const solveIntervalRef = useRef(null);

  const getSolutionSteps = (n, src, aux, dest) => {
      if (n === 1) {
          return [[src, dest]];
      }
      const steps1 = getSolutionSteps(n - 1, src, dest, aux);
      const steps2 = [[src, dest]];
      const steps3 = getSolutionSteps(n - 1, aux, src, dest);
      return [...steps1, ...steps2, ...steps3];
  };

  const startAutoSolve = () => {
      // Hard reset first to ensure clean state? 
      // Or solve from current state? (Recursion complex from partial state).
      // Let's reset for simplicity.
      
      // We will perform reset inside a promise or immediately, then start.
      // But React state updates are async.
      // We'll calculate steps for a FULL visual solve.
      
      const newStartTower = [];
      for (let i = numDisks; i >= 1; i--) newStartTower.push(i);
      
      // Update state for visual reset
      setTowers([newStartTower, [], []]);
      setMoves(0);
      setGameWon(false);
      setSelectedTower(null);
      setIsSolving(true);

      const steps = getSolutionSteps(numDisks, 0, 1, 2); // 0=Src, 1=Aux, 2=Dest
      solveQueueRef.current = steps;

      if (solveIntervalRef.current) clearInterval(solveIntervalRef.current);
      
      solveIntervalRef.current = setInterval(() => {
          if (solveQueueRef.current.length === 0) {
              stopAutoSolve();
              setGameWon(true);
              return;
          }
          const [from, to] = solveQueueRef.current.shift();
          
          setTowers(prev => {
              const newTowers = prev.map(t => [...t]);
              const disk = newTowers[from].pop();
              newTowers[to].push(disk);
              return newTowers;
          });
          setMoves(prev => prev + 1);

      }, 500); // 500ms per move
  };

  const stopAutoSolve = () => {
      if (solveIntervalRef.current) {
          clearInterval(solveIntervalRef.current);
          solveIntervalRef.current = null;
      }
      setIsSolving(false);
  };

  // Cleanup
  useEffect(() => {
      return () => stopAutoSolve();
  }, []);


  // --------------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------------
  
  // Calculate disk width based on size. size 1 = 30px, each step + 20px
  const getDiskWidth = (size) => Math.max(40, size * 25 + 20);
  const getDiskColor = (size) => {
      const colors = [
          "bg-red-500", "bg-orange-500", "bg-yellow-500", "bg-green-500", 
          "bg-teal-500", "bg-blue-500", "bg-indigo-500", "bg-purple-500"
      ];
      return colors[(size - 1) % colors.length];
  };

  return (
    <div className="max-w-4xl mx-auto p-4">
      <div className="mb-4">
        <button
          onClick={() => (onBack ? onBack() : navigate("/games"))}
          className="flex items-center text-blue-600 hover:text-blue-800 font-medium"
        >
          ← Back to Games
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-xl p-8">
        <h1 className="text-3xl font-bold text-center mb-2 text-gray-800">Tower of Hanoi</h1>
        <p className="text-center text-gray-600 mb-8">
             Move the entire stack to the last rod. One disk at a time, never larger on smaller.
        </p>

        {/* Controls */}
        <div className="flex flex-wrap justify-center items-center gap-6 mb-12">
            <div className="flex items-center gap-3 bg-gray-100 px-4 py-2 rounded-lg">
                <span className="font-semibold text-gray-700">Disks:</span>
                <div className="flex gap-1">
                    {[3, 4, 5, 6, 7, 8].map(n => (
                        <button
                            key={n}
                            onClick={() => { if(!isSolving) setNumDisks(n); }}
                            className={`w-8 h-8 rounded-full font-bold text-sm transition-colors ${
                                numDisks === n ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-200'
                            } ${isSolving ? 'opacity-50 cursor-not-allowed' : ''}`}
                            disabled={isSolving}
                        >
                            {n}
                        </button>
                    ))}
                </div>
            </div>

            <div className="flex items-center gap-3">
                 <div className="bg-gray-100 px-4 py-2 rounded-lg font-mono">
                     Moves: <span className="font-bold text-blue-600">{moves}</span> / Min: {minMoves}
                 </div>
                 <button 
                     onClick={() => resetGame(numDisks)}
                     className="p-2 bg-gray-200 rounded-full hover:bg-gray-300 transition-colors"
                     title="Reset Game"
                 >
                     <RotateCcw className="w-5 h-5 text-gray-700" />
                 </button>
            </div>
            
            <button 
                onClick={isSolving ? stopAutoSolve : startAutoSolve}
                className={`
                    flex items-center gap-2 px-5 py-2 rounded-full font-bold text-white transition-all shadow-md
                    ${isSolving ? 'bg-red-500 hover:bg-red-600' : 'bg-green-600 hover:bg-green-700'}
                `}
            >
                {isSolving ? (
                    <>Stop</>
                ) : (
                    <><Play className="w-4 h-4" /> Auto Solve</>
                )}
            </button>
        </div>

        {/* Game Area */}
        <div className="relative h-64 md:h-80 mx-auto w-full max-w-3xl select-none">
            {/* Base */}
            <div className="absolute bottom-0 w-full h-4 bg-gray-800 rounded-full"></div>

            {/* Rods */}
            <div className="flex justify-around h-full items-end pb-4">
                {[0, 1, 2].map(idx => (
                    <div 
                        key={idx}
                        className={`
                            relative w-1/3 h-full flex flex-col justify-end items-center group cursor-pointer
                        `}
                        onClick={() => handleTowerClick(idx)}
                    >
                         {/* Rod Stick */}
                         <div className={`
                             absolute bottom-0 w-4 h-[90%] rounded-t-lg transition-colors duration-200 pointer-events-none
                             ${selectedTower === idx ? 'bg-blue-400' : 'bg-gray-300'}
                             ${!gameWon && !isSolving && (selectedTower === idx || (selectedTower === null && towers[idx].length > 0) || (selectedTower!==null && isValidMove(selectedTower, idx))) ? 'group-hover:bg-blue-300' : ''}
                         `}></div>
                         
                         {/* Disks */}
                         <div className="z-10 flex flex-col-reverse items-center w-full mb-0">
                             {towers[idx].map((size, index) => {
                                 const isTop = index === towers[idx].length - 1;
                                 const isSelected = selectedTower === idx && isTop;
                                 
                                 return (
                                     <div 
                                         key={size}
                                         className={`
                                             h-6 md:h-8 rounded-lg shadow-md border border-black/10 transition-all duration-300
                                             ${getDiskColor(size)}
                                             ${isSelected ? '-translate-y-8 shadow-xl brightness-110' : ''}
                                         `}
                                         style={{ width: `${getDiskWidth(size)}px` }}
                                     ></div>
                                 );
                             })}
                         </div>

                         {/* Hover Target (Invisible clickable area) */}
                         <div className="absolute inset-0 z-20"></div>
                    </div>
                ))}
            </div>

            {gameWon && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/50 backdrop-blur-sm rounded-xl animate-fade-in z-30">
                    <div className="bg-white p-6 rounded-2xl shadow-2xl text-center border-4 border-yellow-400 transform scale-125">
                         <div className="text-4xl mb-2">🎉</div>
                         <h2 className="text-2xl font-bold text-gray-800 mb-1">Puzzle Solved!</h2>
                         <p className="text-gray-600 text-sm">Ideally in {minMoves} moves.</p>
                         <button 
                             onClick={() => resetGame(numDisks)}
                             className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700"
                         >
                             Play Again
                         </button>
                    </div>
                </div>
            )}
        </div>
        
      </div>
    </div>
  );
};

export default TowerOfHanoiGame;
